/**
 * Init preset — the patch the synth boots into. main.ts uses
 * `applyInitPresetParams` (below) to dial in the params at boot
 * without going through the full PresetManager.applyState path,
 * which is intentionally aggressive (rebuilds the FX chain, clears
 * mod connections, etc.) and overkill for "set the starting knob
 * positions". This narrow apply only deserializes module + effect
 * params on the existing runtime objects — voice settings, BPM, mod
 * connections, and macros are left alone since they already match
 * the runtime defaults.
 *
 * Body of this file is the literal JSON the user uploaded — kept
 * verbatim so editing it (or swapping it for a future export) is a
 * straight copy-paste.
 */

import type { Preset } from '@ac-slayer/synth-presets';
import type { EffectsChain } from '@ac-slayer/synth-core';
import type { SynthModule } from '@ac-slayer/synth-core';

export const INIT_PRESET: Preset = {
  id: 'preset-537bdf92',
  bpm: 120,
  name: 'Init',
  author: 'init',
  tags: [],
  voice: {
    mode: 'poly',
    unison: 1,
    glideMs: 0,
    polyphony: 8,
    unisonSpread: 15,
  },
  macros: [0, 0, 0, 0],
  effects: [
    {
      id: 'compressor',
      type: 'compressor',
      bypass: false,
      params: {
        Ratio: 4,
        Attack: 0.01,
        Makeup: 1,
        Release: 0.2,
        Threshold: -24,
      },
    },
    {
      id: 'dist',
      type: 'distortion',
      bypass: false,
      params: {
        Mix: 0.22318359375000002,
        Tone: 5000,
        Drive: 0.06046874999999999,
      },
    },
    {
      id: 'ringmod',
      type: 'ringmod',
      bypass: false,
      params: {
        Mix: 0.03759765624999997,
        Freq: 200,
      },
    },
    {
      id: 'chorus',
      type: 'chorus',
      bypass: true,
      params: {
        Mix: 0.4,
        Rate: 0.6,
        Depth: 0.5,
      },
    },
    {
      id: 'flanger',
      type: 'flanger',
      bypass: true,
      params: {
        Mix: 0.5,
        Rate: 0.3,
        Depth: 0.6,
        Feedback: 0.5,
      },
    },
    {
      id: 'phaser',
      type: 'phaser',
      bypass: true,
      params: {
        Mix: 0.5,
        Rate: 0.4,
        Depth: 0.6,
        Feedback: 0.4,
      },
    },
    {
      id: 'delay',
      type: 'delay',
      bypass: false,
      params: {
        Mix: 0.25,
        Sync: 'free',
        Time: 0.28,
        Pitch: 0,
        Feedback: 0.45,
      },
    },
    {
      id: 'bitcrusher',
      type: 'bitcrusher',
      bypass: false,
      params: {
        Mix: 0.09558593750000001,
        Bits: 12,
        Freq: 16000,
      },
    },
    {
      id: 'reverb',
      type: 'reverb',
      bypass: false,
      params: {
        Mix: 0.25,
        Damp: 6000,
        Size: 1.8,
        Pitch: 0,
      },
    },
  ],
  modules: [
    {
      id: 'osc',
      type: 'oscillator',
      bypass: false,
      params: {
        Fold: 2,
        Wave: 'saw',
        Drive: 0,
        Level: 0.8,
        Width: 0.5,
        Coarse: -12,
        Detune: 0,
      },
    },
    {
      id: 'filter',
      type: 'filter',
      bypass: false,
      params: {
        Q: 4,
        Type: 'lowpass',
        Cutoff: 1800,
      },
    },
    {
      id: 'vca',
      type: 'vca',
      bypass: false,
      params: {
        Gain: 0.85,
      },
    },
    {
      id: 'env',
      type: 'envelope',
      bypass: false,
      params: {
        Decay: 0.25,
        Attack: 0.005,
        Release: 0.35,
        Sustain: 0.55,
      },
    },
    {
      id: 'lfo',
      type: 'lfo',
      bypass: false,
      params: {
        Rate: 2.5,
        Sync: 'free',
        Wave: 'triangle',
      },
    },
    {
      id: 'sh',
      type: 'sh',
      bypass: false,
      params: {
        Rate: 6,
        Slew: 0,
      },
    },
    {
      id: 'noise',
      type: 'noise',
      bypass: false,
      params: {
        Color: 'white',
        Level: 0,
      },
    },
    {
      id: 'arp',
      type: 'arp',
      bypass: false,
      params: {
        Gate: 0.5,
        Mode: 'up',
        Rate: '1/8',
        Enabled: 'off',
        Octaves: 1,
      },
    },
    {
      id: 'sequencer',
      type: 'sequencer',
      bypass: false,
      params: {
        Rate: '1/16',
        Swing: 0,
        steps:
          '[{"active":true,"note":60,"velocity":0.8,"length":1,"modValue":0.5},{"active":true,"note":63,"velocity":0.8,"length":1,"modValue":0.6913417161825449},{"active":true,"note":65,"velocity":0.8,"length":1,"modValue":0.8535533905932737},{"active":true,"note":67,"velocity":0.8,"length":1,"modValue":0.9619397662556434},{"active":true,"note":70,"velocity":0.8,"length":1,"modValue":1},{"active":true,"note":72,"velocity":0.8,"length":1,"modValue":0.9619397662556434},{"active":true,"note":70,"velocity":0.8,"length":1,"modValue":0.8535533905932737},{"active":true,"note":67,"velocity":0.8,"length":1,"modValue":0.6913417161825449},{"active":true,"note":65,"velocity":0.8,"length":1,"modValue":0.5000000000000001},{"active":true,"note":63,"velocity":0.8,"length":1,"modValue":0.3086582838174552},{"active":true,"note":60,"velocity":0.8,"length":1,"modValue":0.14644660940672627},{"active":true,"note":58,"velocity":0.8,"length":1,"modValue":0.03806023374435674},{"active":true,"note":60,"velocity":0.8,"length":1,"modValue":0},{"active":true,"note":63,"velocity":0.8,"length":1,"modValue":0.038060233744356686},{"active":true,"note":65,"velocity":0.8,"length":1,"modValue":0.14644660940672616},{"active":true,"note":67,"velocity":0.8,"length":1,"modValue":0.3086582838174548},{"active":false,"note":60,"velocity":0.8,"length":1,"modValue":0.4999999999999999},{"active":false,"note":63,"velocity":0.8,"length":1,"modValue":0.691341716182545},{"active":false,"note":65,"velocity":0.8,"length":1,"modValue":0.8535533905932737},{"active":false,"note":67,"velocity":0.8,"length":1,"modValue":0.9619397662556433},{"active":false,"note":70,"velocity":0.8,"length":1,"modValue":1},{"active":false,"note":72,"velocity":0.8,"length":1,"modValue":0.9619397662556434},{"active":false,"note":70,"velocity":0.8,"length":1,"modValue":0.8535533905932742},{"active":false,"note":67,"velocity":0.8,"length":1,"modValue":0.6913417161825453},{"active":false,"note":65,"velocity":0.8,"length":1,"modValue":0.5000000000000002},{"active":false,"note":63,"velocity":0.8,"length":1,"modValue":0.3086582838174551},{"active":false,"note":60,"velocity":0.8,"length":1,"modValue":0.14644660940672605},{"active":false,"note":58,"velocity":0.8,"length":1,"modValue":0.0380602337443568},{"active":false,"note":60,"velocity":0.8,"length":1,"modValue":0},{"active":false,"note":63,"velocity":0.8,"length":1,"modValue":0.03806023374435663},{"active":false,"note":65,"velocity":0.8,"length":1,"modValue":0.14644660940672577},{"active":false,"note":67,"velocity":0.8,"length":1,"modValue":0.3086582838174547}]',
        Length: 16,
        Enabled: 'off',
      },
    },
  ],
  category: 'Uncategorized',
  createdAt: 1777232366201,
  updatedAt: 1777234113527,
  effectsOrder: [
    'compressor',
    'distortion',
    'ringmod',
    'chorus',
    'flanger',
    'phaser',
    'delay',
    'bitcrusher',
    'reverb',
  ],
  midiBindings: [],
  schemaVersion: 5,
  modConnections: [
    {
      id: 'conn-da33e9d7',
      depth: 0.4,
      targetParam: 'Cutoff',
      sourceModuleId: 'lfo',
      targetModuleId: 'filter',
    },
    {
      id: 'conn-b19a80f9',
      depth: 0.3,
      targetParam: 'Cutoff',
      sourceModuleId: 'velocity',
      targetModuleId: 'filter',
    },
  ],
};

/**
 * Narrowest possible "load this preset" — only deserializes module
 * and effect params. Skips:
 *   - BPM, voice settings (they already match runtime defaults)
 *   - Mod connections (the runtime adds the same defaults)
 *   - Macros (already 0 by default)
 *   - MIDI bindings (the user's local settings should win)
 *   - Effects chain composition / order (the bootstrap already
 *     lays out the same chain in the same order)
 *
 * This avoids the heavier rebuild paths (effectsChain.applyPreset,
 * router.removeConnection in a loop) that broke things last time we
 * tried PresetManager.applyState at boot. Any individual deserialize
 * failure is logged and skipped — it shouldn't take the whole boot
 * with it.
 */
export function applyInitPresetParams(
  modules: readonly SynthModule[],
  effectsChain: EffectsChain | null,
): void {
  const moduleById = new Map<string, SynthModule>();
  for (const m of modules) moduleById.set(m.id, m);

  for (const state of INIT_PRESET.modules) {
    const m = moduleById.get(state.id);
    if (!m) continue;
    try {
      m.deserialize(state);
    } catch (err) {
      console.warn(`[init-preset] failed to deserialize module ${state.id}`, err);
    }
  }

  if (effectsChain) {
    const fxById = new Map<string, SynthModule>();
    for (const fx of effectsChain.getModules()) fxById.set(fx.id, fx as SynthModule);
    for (const state of INIT_PRESET.effects ?? []) {
      const fx = fxById.get(state.id);
      if (!fx) continue;
      try {
        fx.deserialize(state);
      } catch (err) {
        console.warn(`[init-preset] failed to deserialize effect ${state.id}`, err);
      }
    }
  }
}
